import { useEffect, useState, useRef } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  Button,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  useTheme,
  useMediaQuery
} from "@mui/material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "./landingHeaderStyles.css";
import ryseLogo from "../../assets/images/ryse_icon.png";
import menuIcon from "../../assets/icons/menu-04.svg";
import closeIcon from "../../assets/icons/x-close.svg";
import DataIngestionModal from "../../clientCases/components/DataIngestionModal";
import DataIngestionAfricanModal from "../../clientCases/components/DataIngestionAfricanModal";
import DataIngestionFamilyModal from "../../clientCases/components/DataIngestionFamilyModal";
import DataIngestionPEModal from "../../clientCases/components/DataIngestionPEModal";
import DataIngestionCryptoModal from "../../clientCases/components/DataIngestionCryptoModal";
import DataIngestionIndividualModal from "../../clientCases/components/DataIngestionIndividualModal";
import DataIngestionMiddleIncomeModal from "../../clientCases/components/DataIngestionMiddleIncomeModal";
import QuantAnalysisModal from "../../clientCases/components/QuantAnalysisModal";
import QuantAnalysisAfricanModal from "../../clientCases/components/QuantAnalysisAfricanModal";
import QuantAnalysisFamilyModal from "../../clientCases/components/QuantAnalysisFamilyModal";
import QuantAnalysisPeModal from "../../clientCases/components/QuantAnalysisPeModal";
import QuantAnalysisCryptoModal from "../../clientCases/components/QuantAnalysisCryptoModal";
import QuantAnalysisIndividualModal from "../../clientCases/components/QuantAnalysisIndividualModal";
import QuantAnalysisMiddleIncomeModal from "../../clientCases/components/QuantAnalysisMiddleIncomeModal";
import loadingGif from "../../assets/images/Animation-loading-blue-green.gif";
import appInsights from "../../../applicationInsights";

const REDIRECTS = {
  "inst-manager": {
    url: "/redirections/InstutionalManager_PortfolioAssesment/index.html",
    newTab: true
  },
  "our-portfolio": {
    url: "/redirections/Our_Portfolio/index.html",
    newTab: true
  },
  "agentic-ecosystem": {
    url: "/redirections/AgenticPortfolioManagementEcosystem/index.html",
    newTab: true
  },
  "client-references": {
    url: "/redirections/ClientReferences/index.html",
    newTab: true
  },
  "human-vs-ai-page": {
    url: "https://jbai.ai/cost-of-expertise-humans-vs-ai-agents",
    newTab: true
  },
  "human-vs-ai-article": {
    url: "/articles/JBAI_Article_Template_Nov_12_2024.pdf",
    newTab: true
  },  
  architecture: {
    url: "/architecture-of-platform",
    newTab: false
  },
  robustness: {
    url: "/robustness-of-ryse-methodology",
    newTab: false
  },
  "multi-asset": {
    url: "/multi-asset-class-support",
    newTab: false
  }
};

const LEARN_MORE_ITEMS = {
  "inst-manager": "Institutional Manager - Portfolio Assessment",
  "our-portfolio": "Our Portfolio",
  "agentic-ecosystem": "Agentic Portfolio Management Ecosystem",
  "client-references": "Client References",
  "human-vs-ai-page": "Human vs AI - Web Page",
  "human-vs-ai-article": "Human vs AI - Article"
};

const TECHNICAL_DETAILS_ITEMS = {
  architecture: "Architecture of Platform",
  robustness: "Robustness of Ryse Methodology",
  "multi-asset": "The Multi-Asset Class Support"
};

const BLOB_BASE = "https://rysevideos-cehgedfjh3fbetev.z01.azurefd.net/videos/";

const LIVE_INSTANCE_VIDEOS = {
  "pension-live-defined-benefit": {
    title: "IAM Defined Benefit – Live Instance",
    url: BLOB_BASE + "f_Ryse_IAM_Defined_Benefit_Feb_23_2026_720p.mp4"
  },
  "pension-live-fixed-income": {
    title: "IAM Fixed Income – Live Instance",
    url: BLOB_BASE + "f_Ryse_IAM_Fixed_Income_Portfolio_Feb_24_2026_720p.mp4"
  },
  "pension-live-domestic-equity": {
    title: "IAM Domestic Equity – Live Instance",
    url: BLOB_BASE + "f_Ryse_IAM_Domestic_Equity_Portfolio_Feb_24_2026_720p.mp4" // <-- you likely still need to upload/confirm this file
  },
  "pension-live-absolute-return": {
    title: "IAM Absolute Return – Live Instance",
    url: BLOB_BASE + "f_Ryse_IAM_Absolute_Return_Portfolio_Feb_24_2026_720p.mp4"
  },
  "pension-live-private-equity": {
    title: "IAM Private Equity – Live Instance",
    url: BLOB_BASE + "f_Ryse_IAM_Private_Equity_Portfolio_Feb_24_2026_720p.mp4"
  },
  "pension-live-real-estate": {
    title: "IAM Real Estate – Live Instance",
    url: BLOB_BASE + "f_Ryse_IAM_Real_Estate_Portfolio_Feb_24_2026_720p.mp4"
  },
  "pension-ai-reasoning": {
    title: "AI Interaction – AI Reasoning",
    category: "AIReasoning",
    url: BLOB_BASE + "f_Ryse_AI_Interaction_Feb_28_2026_720p.mp4"
  },
  "african-live-portfolio": {
    title: "African Portfolio – Live Instance",
    url: BLOB_BASE + "f_Ryse_Africa_Portfolio_Feb_24_2026_720p.mp4"
  },
  "african-ai-reasoning": {
    title: "AI Interaction – AI Reasoning",
    category: "AIReasoning",
    url: BLOB_BASE + "f_Ryse_Africa_AI_Interaction_Feb_28_2026_720p.mp4"
  },
  "family-live-portfolio": {
    title: "Family Portfolio – Live Instance",
    url: BLOB_BASE + "f_Ryse_AMFO_Portfolio_Feb_25_2026_720p.mp4"
  },
  "family-ai-reasoning": {
    title: "Family Portfolio – AI Reasoning",
    category: "AIReasoning",
    url: BLOB_BASE + "f_Ryse_AMFO_Portfolio_AI_Interaction_Feb_28_2026_720p.mp4"
  },
  "family-conference": {
    title: "Family Office – Quant Conference",
    category: "QuantConference",
    url: BLOB_BASE + "f_Bloomberg_Quant_Seminar_May_25_720p.mp4"
  },
  "PE-live-portfolio": {
    title: "Private Equity Portfolio – Live Instance",
    url: BLOB_BASE + "f_Ryse_Priv_Equity_Portfolio_Feb_25_2026_720p.mp4"
  },
  "PE-ai-reasoning": {
    title: "Private Equity Portfolio – AI Reasoning",
    category: "AIReasoning",
    url: BLOB_BASE + "f_Ryse_Priv_Equity_AI_Interaction_Portfolio_Feb_28_2026_720p.mp4"
  },
  "Crypto-live-portfolio": {
    title: "Crypto FoF Portfolio – Live Instance",
    url: BLOB_BASE + "f_Ryse_Crypto_Portfolio_Feb_20_2026_720p.mp4"
  },
  "crypto-ai-reasoning": {
    title: "Crypto FoF Portfolio – AI Reasoning",
    category: "AIReasoning",
    url: BLOB_BASE + "f_Ryse_Crypto_Portfolio_AI_Interaction_Feb_28_2026_720p.mp4"
  },
  "Individual-live-portfolio": {
    title: "Individual Portfolio – Live Instance",
    //url: BLOB_BASE + "f_Ryse_AI_Interaction_720p.mp4"
    url: BLOB_BASE + "f_Ryse_Individual_Portfolio_Feb_25_2026_720p.mp4"
  },
  "individual-ai-reasoning": {
    title: "Individual Portfolio – AI Reasoning",
    category: "AIReasoning",
    url: BLOB_BASE + "f_Ryse_PWM_AI_Interaction_Mar_02_2026_720p.mp4"
  },
  "middle-live-portfolio": {
    title: "Middle Income Portfolio – Live Instance",
    url: BLOB_BASE + "f_Ryse_Middle_Inc_Portfolio_Feb_25_2026_720p.mp4"
  },
  "middle-ai-reasoning": {
    title: "Middle Income Portfolio – AI Reasoning",
    category: "AIReasoning",
    url: BLOB_BASE + "f_Ryse_Middle_Inc_Portfolio_AI_Agent_Mar_02_2026_720p.mp4"
  }
};

const CLIENT_IMPLEMENTATION_CONTENT = {
  "pension-data-ingestion": { clientType: "PensionFund", contentType: "DataIngestion" },
  "pension-quant-analytics": { clientType: "PensionFund", contentType: "QuantAnalytics" },
  "african-data-ingestion": { clientType: "AfricanSocialSecurityFund", contentType: "DataIngestion" },
  "african-quant-analytics": { clientType: "AfricanSocialSecurityFund", contentType: "QuantAnalytics" },
  "family-data-ingestion": { clientType: "FamilyOffice", contentType: "DataIngestion" },
  "family-quant-analytics": { clientType: "FamilyOffice", contentType: "QuantAnalytics" },
  "PE-data-ingestion": { clientType: "PrivateEquityFund", contentType: "DataIngestion" },
  "PE-quant-analytics": { clientType: "PrivateEquityFund", contentType: "QuantAnalytics" },
  "crypto-data-ingestion": { clientType: "CryptoFund", contentType: "DataIngestion" },
  "crypto-quant-analytics": { clientType: "CryptoFund", contentType: "QuantAnalytics" },
  "individual-data-ingestion": { clientType: "PrivateWealthManagementClient", contentType: "DataIngestion" },
  "individual-quant-analytics": { clientType: "PrivateWealthManagementClient", contentType: "QuantAnalytics" },
  "middle-data-ingestion": { clientType: "MiddleIncomeUser", contentType: "DataIngestion" },
  "middle-quant-analytics": { clientType: "MiddleIncomeUser", contentType: "QuantAnalytics" }
};

const NAV_ITEMS = [
  {
    id: "home",
    label: "Home",
    onClick: (path) => {
      if (path === "/") {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }
  },
  {
    id: "technicalDetails",
    label: "Technical Details",
    children: [
      { id: "architecture", label: "Architecture of Platform" },
      { id: "robustness", label: "Robustness of Ryse Methodology" },
      { id: "multi-asset", label: "The Multi-Asset Class Support" }
    ]
  },
  {
    id: "learnMore",
    label: "Learn More about Ryse",
    children: [
      {
        id: "inst-manager",
        label: "Institutional Manager - Portfolio Assessment"
      },
      { id: "our-portfolio", label: "Our Portfolio" },
      {
        id: "agentic-ecosystem",
        label: "Agentic Portfolio Management Ecosystem"
      },
      {
        id: "client-references",
        label: "Client References"
      },
      { 
        id: "human-vs-ai", 
        label: "Human vs AI",
        children: [
        { id: "human-vs-ai-page", label: "Web Page" },
        { id: "human-vs-ai-article", label: "Article" }
        ]
      }
    ]
  },
  {
    id: "clientImplementations",               
    label: "Typical Client Implementations",   
    children: [
        { id: "pension", label: "Pension Fund",
          children: [
            { id: "pension-data-ingestion", label: "Data Ingestion" },
            { id: "pension-quant-analytics", label: "Quant Analytics" },
            { id: "pension-ai-reasoning", label: "AI Reasoning" },
            {
              id: "pension-live-instance",
              label: "Live Instance",
              children: [
                { id: "pension-live-defined-benefit", label: "IAM Defined Benefit" },
                { id: "pension-live-fixed-income", label: "IAM Fixed Income" },
                { id: "pension-live-domestic-equity", label: "IAM Domestic Equity" },
                { id: "pension-live-absolute-return", label: "IAM Absolute Return" },
                { id: "pension-live-private-equity", label: "IAM Private Equity" },
                { id: "pension-live-real-estate", label: "IAM Real Estate" }
              ]
            }
          ]          
         },
        { id: "African-Soc-Sec_Fund", label: "African Soc. Sec. Fund",
          children: [
            { id: "african-data-ingestion", label: "Data Ingestion" },
            { id: "african-quant-analytics", label: "Quant Analytics" },
            { id: "african-ai-reasoning", label: "AI Reasoning" },
            {
              id: "african-live-instance",
              label: "Live Instance",
              children: [
                { id: "african-live-portfolio", label: "African Institutional Portfolio" },
              ]
            }
          ]          
         },
        { id: "family-office", label: "Family Office",
          children: [
            { id: "family-data-ingestion", label: "Data Ingestion" },
            { id: "family-quant-analytics", label: "Quant Analytics" },
            { id: "family-ai-reasoning", label: "AI Reasoning" },
            {
              id: "family-live-instance",
              label: "Live Instance",
              children: [
                { id: "family-live-portfolio", label: "Family Office Portfolio" },
                { id: "family-conference", label: "Bloomberg Quant Conference" },
              ]
            }
          ]
         },
        { id: "private-equity", label: "Private Equity Fund",
          children: [
            { id: "PE-data-ingestion", label: "Data Ingestion" },
            { id: "PE-quant-analytics", label: "Quant Analytics" },
            { id: "PE-ai-reasoning", label: "AI Reasoning" },
            {
              id: "PE-live-instance",
              label: "Live Instance",
              children: [
                { id: "PE-live-portfolio", label: "Private Equity Portfolio" },
              ]
            }

          ] },
        { id: "crypto", label: "Crypto Fund",
          children: [
            { id: "crypto-data-ingestion", label: "Data Ingestion" },
            { id: "crypto-quant-analytics", label: "Quant Analytics" },
            { id: "crypto-ai-reasoning", label: "AI Reasoning" },
            {
              id: "crypto-live-instance",
              label: "Live Instance",
              children: [
                { id: "Crypto-live-portfolio", label: "Crypto FoF Portfolio" },
              ]
            }
          ] },
        { id: "individual", label: "Private Wealth Mgt Client",
          children: [
            { id: "individual-data-ingestion", label: "Data Ingestion" },
            { id: "individual-quant-analytics", label: "Quant Analytics" },
            { id: "individual-ai-reasoning", label: "AI Reasoning" },
            {
              id: "individual-live-instance",
              label: "Live Instance",
              children: [
                { id: "Individual-live-portfolio", label: "John Smith's Portfolio Including Other Inv't Products" },
              ]
            }
          ] },
        { id: "Middle-Income", label: "Middle Income User",
          children: [
            { id: "middle-data-ingestion", label: "Data Ingestion" },
            { id: "middle-quant-analytics", label: "Quant Analytics" },
            { id: "middle-ai-reasoning", label: "AI Reasoning" },
            {
              id: "middle-live-instance",
              label: "Live Instance",
              children: [
                { id: "middle-live-portfolio", label: "Portfolio of Individual Incl. Equities, Fixed Inc. & Real Estate" },
              ]
            }
          ] }
    ]
  }
];

// rutas que activan "Technical Details"
const TECH_PATHS = [
  "/architecture-of-platform",
  "/robustness-of-ryse-methodology",
  "/multi-asset-class-support"
];

function LiveVideoModal({ open, onClose, title, url, videoId, category }) {
  const videoRef = useRef(null);
  const hasTrackedPlayRef = useRef(false);
  const [progressPct, setProgressPct] = useState(null);   // 👈 HERE
  const [statusText, setStatusText] = useState("Connecting...");
  const [isReady, setIsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [wantsPlay, setWantsPlay] = useState(false);
  // NEW: video shape info
  const [isPortrait, setIsPortrait] = useState(false);
  // reset state each time modal opens / url changes
  useEffect(() => {
    if (!open) return;
    setIsReady(false);
    setIsLoading(true);
    setWantsPlay(false);
    setIsPortrait(false);
    hasTrackedPlayRef.current = false;
    // kick the browser to start fetching metadata asap
    queueMicrotask(() => {
      try {
        videoRef.current?.load();
      } catch {}
    });
  }, [open, url]);

  
  // when video becomes ready, stop showing the GIF
  const handleCanPlay = () => {
    setIsReady(true);
    setIsLoading(false);
    setStatusText("Ready");
    // If you want autoplay AFTER ready (silent), uncomment below:
    // if (videoRef.current) {
    //   videoRef.current.muted = true;
    //   videoRef.current.play().catch(() => {});
    // }
  };

  const handleWaiting = () => {
    // if buffering happens mid-play, show GIF again if you want
    setIsLoading(true);
    setStatusText("Buffering...");
  };

  const handlePlaying = () => {
    setIsReady(true);
    setIsLoading(false);
    setStatusText("Playing");

    if (hasTrackedPlayRef.current) return;
    hasTrackedPlayRef.current = true;

    appInsights.trackEvent({
      name: "VideoPlayed",
      properties: {
        videoId,
        title,
        category,
        url,
      },
    });
  };
  // progress estimate (buffered / duration)
  const handleProgress = (e) => {
    const v = e.currentTarget;
    if (!v?.duration || !Number.isFinite(v.duration)) return;
    if (!v.buffered || v.buffered.length === 0) return;

    const end = v.buffered.end(v.buffered.length - 1);
    const pct = Math.max(0, Math.min(100, (end / v.duration) * 100));

    setProgressPct(pct);
    if (!isReady) setStatusText("Loading...");
  };

  const handleLoadedMetadata = () => {
    setStatusText("Loading...");
    const v = videoRef.current;
    if (!v) return;

    const w = v.videoWidth || 0;
    const h = v.videoHeight || 0;
    if (!w || !h) return;

    // portrait if taller than wide
    setIsPortrait(h > w);
  };

  const handleClose = () => {
    // stop video when closing
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
    onClose();
  };

  if (!open) return null;
  // NEW: dynamic sizing
  const contentWidth = isPortrait ? "min(520px, 92vw)" : "min(1100px, 96vw)";
  const videoHeight = isPortrait ? "80vh" : "70vh";
  return (
    <div className="video-modal-overlay" onClick={handleClose}>
      <div className="video-modal-content" onClick={(e) => e.stopPropagation()}>

        {/* ✅ Header row: Title + Status (left), X (right) */}
        <div
          style={{
            padding: "10px 0",
            display: "flex",
            alignItems: "center",
            gap: 10
          }}
        >
          {/* Left side: title + status */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
            <div style={{ fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {title}
            </div>

            <div
              style={{
                fontSize: 12,
                padding: "6px 10px",
                borderRadius: 999,
                background: "rgba(0,0,0,0.35)",
                color: "#fff",
                whiteSpace: "nowrap"
              }}
            >
              {isReady
                ? "Ready"
                : progressPct !== null
                ? `Loading ${Math.round(progressPct)}%`
                : statusText}
            </div>
          </div>

          {/* Right side: close button */}
          <button
            onClick={handleClose}
            style={{
              marginLeft: "auto",
              width: 32,
              height: 32,
              borderRadius: 8,
              border: "none",
              background: "rgba(0,0,0,0.25)",
              color: "#fff",
              fontSize: 20,
              cursor: "pointer"
            }}
            aria-label="Close"
          >
            ×
          </button>
        </div>

      <div style={{ position: "relative" }}>
          {/* VIDEO (hidden until ready so no big Play overlay shows) */}
          <video
            ref={videoRef}
            src={url}
            controls={isReady}
            playsInline
            preload="auto"
            controlsList="nodownload"
            onLoadedMetadata={handleLoadedMetadata}
            onProgress={handleProgress}
            onCanPlay={handleCanPlay}
            onLoadedData={handleCanPlay}
            onWaiting={handleWaiting}
            onPlaying={handlePlaying}
            style={{
              width: "100%",
              maxHeight: "70vh",
              borderRadius: 12,
              opacity: isReady ? 1 : 0,
              pointerEvents: isReady ? "auto" : "none",
              position: isReady ? "relative" : "absolute",
              inset: isReady ? "auto" : 0
            }}
          />
          {/* GIF while loading initial */}
          {!isReady && (
            <img
              src={loadingGif}
              alt="Loading…"
              style={{
                width: "100%",
                maxHeight: "70vh",
                objectFit: "contain",
                borderRadius: 12,
                display: "block"
              }}
            />
          )}

          {/* OPTIONAL: your own Play button (only after ready) */}
          {isReady && !wantsPlay && (
            <button
              onClick={() => {
                setWantsPlay(true);
                videoRef.current?.play().catch(() => {});
              }}
              style={{
                position: "absolute",
                inset: 0,
                margin: "auto",
                width: 90,
                height: 90,
                borderRadius: 999,
                border: "none",
                cursor: "pointer",
                fontSize: 28,
                fontWeight: 700,
                background: "rgba(0,0,0,0.45)",
                color: "white"
              }}
            >
              ▶
            </button>
          )}

          {/* OPTIONAL: show GIF again if buffering mid-play */}
          {isReady && isLoading && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "grid",
                placeItems: "center",
                background: "rgba(0,0,0,0.15)",
                borderRadius: 12
              }}
            >
              <img src={loadingGif} alt="Buffering…" style={{ width: 120 }} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


export default function LandingHeader() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const location = useLocation();
  const navigate = useNavigate();

  const [openMenuId, setOpenMenuId] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openSubMenuId, setOpenSubMenuId] = useState(null);

  const [openThirdMenuId, setOpenThirdMenuId] = useState(null);
  const [liveVideoOpen, setLiveVideoOpen] = useState(false);
  const [liveVideoUrl, setLiveVideoUrl] = useState("");
  const [liveVideoTitle, setLiveVideoTitle] = useState("");
  const [liveVideoId, setLiveVideoId] = useState("");
  const [liveVideoCategory, setLiveVideoCategory] = useState("");
  const thirdCloseTimerRef = useRef(null);
// STEP 2 (add here)
  const [ingestionOpen, setIngestionOpen] = useState(false);
  const [africanIngestionOpen, setAfricanIngestionOpen] = useState(false);
  const [familyIngestionOpen, setFamilyIngestionOpen] = useState(false);
  const [peIngestionOpen, setPEIngestionOpen] = useState(false);
  const [cryptoIngestionOpen, setCryptoIngestionOpen] = useState(false);
  const [individualIngestionOpen, setIndividualIngestionOpen] = useState(false);
  const [middleincomeIngestionOpen, setMiddleIncomeIngestionOpen] = useState(false);
  const [quantOpen, setQuantOpen] = useState(false);
  const [africanQuantOpen, setAfricanQuantOpen] = useState(false);
  const [familyQuantOpen, setFamilyQuantOpen] = useState(false);
  const [peQuantOpen, setPeQuantOpen] = useState(false);
  const [cryptoQuantOpen, setCryptoQuantOpen] = useState(false);
  const [individualQuantOpen, setIndividualQuantOpen] = useState(false);
  const [middleQuantOpen, setMiddleQuantOpen] = useState(false);


  const isHomeActive = location.pathname === "/";
  const isTechnicalActive = TECH_PATHS.includes(location.pathname);
  const [mobileExpandedId, setMobileExpandedId] = useState(null); // expands Pension Fund, etc
  const [mobileExpandedSubId, setMobileExpandedSubId] = useState(null); // expands Live Instance (inside Pension)
  
  const openThirdMenu = (id) => {
    if (thirdCloseTimerRef.current) clearTimeout(thirdCloseTimerRef.current);
    setOpenThirdMenuId(id);
  };

  const scheduleCloseThirdMenu = () => {
    if (thirdCloseTimerRef.current) clearTimeout(thirdCloseTimerRef.current);
    thirdCloseTimerRef.current = setTimeout(() => {
      setOpenThirdMenuId(null);
    }, 200); // <-- tweak 150-300ms to taste
  };

  const handleLogoClick = () => {
    if (location.pathname === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/");
    }
  };

  const openRedirectFn = (childId) => {
    const cfg = REDIRECTS[childId];
    if (!cfg) return;

    if (cfg.newTab) {
      window.open(cfg.url, "_blank", "noopener,noreferrer");
    } else {
      navigate(cfg.url);
    }
  };

  const handleTopItemClick = (item, hasChildren) => {
    if (!hasChildren) {
      item.onClick?.(location.pathname);
      setOpenMenuId(null);
      return;
    }
    setOpenMenuId((prev) => (prev === item.id ? null : item.id));
  };


  const handleLeafClick = (id) => {
    setOpenMenuId(null);
    setOpenSubMenuId(null);
    setOpenThirdMenuId(null);

    const implementationContent = CLIENT_IMPLEMENTATION_CONTENT[id];
    if (implementationContent) {
      appInsights.trackEvent({
        name: "ClientImplementationClicked",
        properties: {
          itemId: id,
          clientType: implementationContent.clientType,
          contentType: implementationContent.contentType
        }
      });
    }

    const learnMoreLabel = LEARN_MORE_ITEMS[id];
    if (learnMoreLabel) {
      const redirect = REDIRECTS[id];
      appInsights.trackEvent({
        name: "LearnMoreClicked",
        properties: {
          itemId: id,
          label: learnMoreLabel,
          url: redirect.url,
          opensInNewTab: redirect.newTab
        }
      });
    }

    const technicalDetailsLabel = TECHNICAL_DETAILS_ITEMS[id];
    if (technicalDetailsLabel) {
      const redirect = REDIRECTS[id];
      appInsights.trackEvent({
        name: "TechnicalDetailsClicked",
        properties: {
          itemId: id,
          label: technicalDetailsLabel,
          url: redirect.url
        }
      });
    }

    const cfg = LIVE_INSTANCE_VIDEOS[id];
    if (cfg) {
      setLiveVideoTitle(cfg.title);
      setLiveVideoUrl(cfg.url);
      setLiveVideoId(id);
      setLiveVideoCategory(cfg.category || "LiveInstance");
      setLiveVideoOpen(true);
      return;
    }

    if (id === "pension-data-ingestion") {
      setIngestionOpen(true);
      return;
    }
    if (id === "pension-quant-analytics") {
      setQuantOpen(true);
      return;
    }

    if (id === "african-data-ingestion") {
      setAfricanIngestionOpen(true);
      return;
    }
    if (id === "african-quant-analytics") {
      setAfricanQuantOpen(true);
      return;
    }

    if (id === "family-data-ingestion") {
      setFamilyIngestionOpen(true);
      return;
    }
    if (id === "family-quant-analytics") {
      setFamilyQuantOpen(true);
      return;
    }
    if (id === "PE-data-ingestion") {
      setPEIngestionOpen(true);
      return;
    }
    if (id === "PE-quant-analytics") {
      setPeQuantOpen(true);
      return;
    }
    if (id === "crypto-data-ingestion") {
      setCryptoIngestionOpen(true);
      return;
    }
    if (id === "crypto-quant-analytics") {
      setCryptoQuantOpen(true);
      return;
    }

    if (id === "individual-data-ingestion") {
      setIndividualIngestionOpen(true);
      return;
    }
    if (id === "individual-quant-analytics") {
      setIndividualQuantOpen(true);
      return;
    }

    if (id === "middle-data-ingestion") {
      setMiddleIncomeIngestionOpen(true);
      return;
    }
    if (id === "middle-quant-analytics") {
      setMiddleQuantOpen(true);
      return;
    }
    // for now, do whatever you want for the others:
    // openRedirectFn(...) or navigate(...) or window.open(...)
    // Example: placeholder
    if (REDIRECTS[id]) {
      openRedirectFn(id);
      return;
    }
    console.log("Clicked:", id);
  };


  return (
    <header className="ryse-header-root content__header-item">
      <AppBar position="fixed" elevation={0} className="ryse-header-appbar ">
        <Toolbar className="ryse-header-toolbar ">
          <Box className="ryse-logo-wrapper" onClick={handleLogoClick} style={{ cursor: "pointer" }}>
            <img src={ryseLogo} alt="Ryse" className="ryse-logo-img" />
          </Box>

          <Box className="ryse-flex-spacer" />

          {isDesktop && (
            <Box className="ryse-nav-desktop ">
              {NAV_ITEMS.map((item) => {
                const hasChildren = !!item.children?.length;

                let isActiveTop = false;
                if (item.label === "Home") isActiveTop = isHomeActive;
                if (item.id === "technicalDetails") isActiveTop = isTechnicalActive;

                return (
                  <Box key={item.id} className="ryse-nav-item-wrapper content__header-item">
                    {item.id === "home" ? (
                      <Link
                        to="/"
                        className={`ryse-nav-button ryse-link-no-style content__header content__header-item ${
                          isHomeActive ? "ryse-nav-button-active content__header-item-active" : ""
                        }`}
                        onClick={() => item.onClick?.(location.pathname)}
                      >
                        {item.label}
                      </Link>
                    ) : (
                      <Button
                        className={`ryse-nav-button content__header-item ${
                          openMenuId === item.id || isActiveTop
                            ? "ryse-nav-button-active content__header-item-active"
                            : ""
                        }`}
                        onClick={() => handleTopItemClick(item, hasChildren)}
                      >
                        {item.label}
                      </Button>
                    )}

                    {hasChildren && openMenuId === item.id && (
                      <Box
                        className="ryse-dropdown"
                        onMouseLeave={() => {
                          setOpenSubMenuId(null);
                          setOpenThirdMenuId(null);
                        }}
                      >
                        <Box className="ryse-dropdown-inner">
                          {item.children.map((child) => {
                            const hasGrandChildren = !!child.children?.length;

                            return (
                              <Box
                                key={child.id}
                                sx={{ position: "relative" }}
                                onMouseEnter={() => {
                                  if (hasGrandChildren) setTimeout(() => setOpenSubMenuId(child.id), 80);
                                }}
                              >
                                <Button
                                  className="ryse-dropdown-item content__header"
                                  onClick={() => {
                                    if (!hasGrandChildren) handleLeafClick(child.id);
                                  }}
                                >
                                  {child.label}
                                </Button>

                                {/* 2nd level */}
                                {hasGrandChildren && openSubMenuId === child.id && (
                                  <Box
                                    sx={{
                                      position: "absolute",
                                      top: 0,
                                      right: "100%",
                                      mr: 1,
                                      bgcolor: "#438D8E",
                                      color: "#111",
                                      py: 1,
                                      minWidth: 180,
                                      zIndex: 2000,
                                      textAlign: "right",
                                      boxShadow: "0 4px 12px rgba(154, 56, 239, 0.6)",
                                      borderRadius: 2
                                    }}
                                    onMouseEnter={() => {
                                      // cancel any scheduled close while inside 2nd-level menu
                                      if (thirdCloseTimerRef.current) clearTimeout(thirdCloseTimerRef.current);
                                    }}                                    
                                    onMouseLeave={scheduleCloseThirdMenu}
                                  >
                                    {child.children.map((gc) => {
                                      const hasGreatGrandChildren = !!gc.children?.length;

                                      return (
                                        <Box
                                          key={gc.id}
                                          sx={{ position: "relative" }}
                                          onMouseEnter={() => {
                                            if (hasGreatGrandChildren) openThirdMenu(gc.id);
                                              else setOpenThirdMenuId(null);
                                          }}
                                          onMouseLeave={scheduleCloseThirdMenu}
                                        >
                                          <Button
                                            onClick={() => {
                                              if (!hasGreatGrandChildren) handleLeafClick(gc.id);
                                            }}
                                            sx={{
                                              justifyContent: "flex-end",
                                              width: "100%",
                                              color: "inherit",
                                              textTransform: "none",
                                              pl: 2,
                                              pr: 1,
                                              "&:hover": { backgroundColor: "rgba(154, 56, 239, 0.6)" }
                                            }}
                                          >
                                            {gc.label}
                                          </Button>

                                          {/* 3rd level: Live Instance 6 videos */}
                                          {hasGreatGrandChildren && openThirdMenuId === gc.id && (
                                            <Box
                                              onMouseEnter={() => openThirdMenu(gc.id)}   // keep open while hovering submenu
                                              onMouseLeave={scheduleCloseThirdMenu}      // close after delay

                                              sx={{
                                                position: "absolute",
                                                top: 0,
                                                right: "100%",
                                                mr: 1,
                                                bgcolor: "#438D8E",
                                                color: "#111",
                                                py: 1,
                                                minWidth: 240,
                                                zIndex: 2500,
                                                textAlign: "right",
                                                boxShadow: "0 4px 12px rgba(154, 56, 239, 0.6)",
                                                borderRadius: 2
                                              }}
                                            >
                                              {gc.children.map((ggc) => (
                                                <Button
                                                  key={ggc.id}
                                                  onClick={() => handleLeafClick(ggc.id)}
                                                  sx={{
                                                    justifyContent: "flex-end",
                                                    width: "100%",
                                                    color: "inherit",
                                                    textTransform: "none",
                                                    pr: 1,
                                                    "&:hover": { backgroundColor: "rgba(154, 56, 239, 0.6)" }
                                                  }}
                                                >
                                                  {ggc.label}
                                                </Button>
                                              ))}
                                            </Box>
                                          )}
                                        </Box>
                                      );
                                    })}
                                  </Box>
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}

          <Box className="ryse-flex-spacer" />

          {!isDesktop && (
            <IconButton edge="end" onClick={() => setMobileOpen(true)}>
              <img src={menuIcon} alt="Menu" className="ryse-menu-icon-img" />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>


      <Drawer
        anchor="top"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        PaperProps={{ className: "ryse-drawer-paper" }}
      >
        <Box className="ryse-drawer-content">
          <Box className="ryse-drawer-header">
            <img                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                              
              src={ryseLogo}
              alt="Ryse"
              className="ryse-logo-img"
              onClick={handleLogoClick}
            />
            <IconButton onClick={() => setMobileOpen(false)}>
              <img
                src={closeIcon}
                alt="Close"
                className="ryse-close-icon-img"
              />
            </IconButton>
          </Box>

          <Divider className="ryse-drawer-divider" />

          <List>
            {NAV_ITEMS.map((item) => {
              const hasChildren = !!item.children?.length;
              let isActiveTop = false;
              if (item.id === "home") isActiveTop = isHomeActive;
              if (item.id === "technicalDetails")
                isActiveTop = isTechnicalActive;

              return (
                <div key={item.id}>
                  {item.id === "home" ? (
                    <ListItemButton
                      onClick={() => {
                        item.onClick?.(location.pathname);
                        navigate("/");
                        setMobileOpen(false);
                      }}
                    >
                      <span
                        className={`ryse-drawer-top-item content__header ${
                          isHomeActive ? "ryse-nav-button-active" : ""
                        }`}
                      >
                        {item.label}
                      </span>
                    </ListItemButton>
                  ) : (
                    <ListItemButton>
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{
                          className: `ryse-drawer-top-item ${
                            isActiveTop ? "ryse-nav-button-active" : ""
                          }`
                        }}
                      />
                    </ListItemButton>
                  )}

                  {item.children?.map((child) => {
                    const hasGrandChildren = !!child.children?.length;
                    const isExpanded = mobileExpandedId === child.id;
                    return (
                    <Box key={child.id}>                    
                    <ListItemButton
                      sx={{ pl: 4 }}
                      onClick={() => {
                        // If it has grandchildren (like Pension Fund), tap expands/collapses
                        if (hasGrandChildren) {
                          setMobileExpandedId(isExpanded ? null : child.id);
                          setMobileExpandedSubId(null); // ✅ reset Live Instance expansion
                          return;
                        }
                        // Leaf node
                        handleLeafClick(child.id);
                        setMobileOpen(false);
                      }}
                    >
                      <ListItemText
                        primary={child.label}
                        primaryTypographyProps={{
                          className: "ryse-drawer-sub-item content__header"
                        }}
                      />
                    </ListItemButton>

                    {/* Render grandchildren when expanded */}
{hasGrandChildren && isExpanded && (
  <Box sx={{ pl: 6 }}>
    {child.children.map((gc) => {
      const hasGreatGrandChildren = !!gc.children?.length;
      const isGcExpanded = mobileExpandedSubId === gc.id;

      return (
        <Box key={gc.id}>
          <ListItemButton
            onClick={() => {
              // If it's "Live Instance" (has children), expand/collapse it
              if (hasGreatGrandChildren) {
                setMobileExpandedSubId(isGcExpanded ? null : gc.id);
                return;
              }

              // Normal leaf (Data Ingestion, Quant Analytics, AI Reasoning)
              handleLeafClick(gc.id);
              setMobileOpen(false);
              setMobileExpandedId(null);
              setMobileExpandedSubId(null);
            }}
          >
            <ListItemText
              primary={gc.label}
              primaryTypographyProps={{
                className: "ryse-drawer-sub-item content__header"
              }}
            />
          </ListItemButton>

                                {/* 3rd level (the 6 videos) */}
                                {hasGreatGrandChildren && isGcExpanded && (
                                  <Box sx={{ pl: 3 }}>
                                    {gc.children.map((ggc) => (
                                      <ListItemButton
                                        key={ggc.id}
                                        onClick={() => {
                                          handleLeafClick(ggc.id); // opens LiveVideoModal because id exists in LIVE_INSTANCE_VIDEOS
                                          setMobileOpen(false);
                                          setMobileExpandedId(null);
                                          setMobileExpandedSubId(null);
                                        }}
                                      >
                                        <ListItemText
                                          primary={ggc.label}
                                          primaryTypographyProps={{
                                            className: "ryse-drawer-sub-item content__header"
                                          }}
                                        />
                                      </ListItemButton>
                                    ))}
                                  </Box>
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      )}

                    </Box>
                    );
                  })}                      

                  <Divider className="ryse-drawer-divider" />
                </div>
              );
            })}
          </List>
        </Box>
      </Drawer>
      {/* Render modal on top of current page */}
      <DataIngestionModal
        open={ingestionOpen}
        onClose={() => setIngestionOpen(false)}
      />
      <QuantAnalysisModal
        open={quantOpen}
        onClose={() => setQuantOpen(false)}
      />

      <DataIngestionAfricanModal
        open={africanIngestionOpen}
        onClose={() => setAfricanIngestionOpen(false)}
      />
      <QuantAnalysisAfricanModal
        open={africanQuantOpen}
        onClose={() => setAfricanQuantOpen(false)}
      />

      <DataIngestionFamilyModal
        open={familyIngestionOpen}
        onClose={() => setFamilyIngestionOpen(false)}
      />
      <QuantAnalysisFamilyModal
        open={familyQuantOpen}
        onClose={() => setFamilyQuantOpen(false)}
      />
      <DataIngestionPEModal
        open={peIngestionOpen}
        onClose={() => setPEIngestionOpen(false)}
      />
      <QuantAnalysisPeModal
        open={peQuantOpen}
        onClose={() => setPeQuantOpen(false)}
      />
      <DataIngestionCryptoModal
        open={cryptoIngestionOpen}
        onClose={() => setCryptoIngestionOpen(false)}
      />
      <QuantAnalysisCryptoModal
        open={cryptoQuantOpen}
        onClose={() => setCryptoQuantOpen(false)}
      />
      <DataIngestionIndividualModal
        open={individualIngestionOpen}
        onClose={() => setIndividualIngestionOpen(false)}
      />
      <QuantAnalysisIndividualModal
        open={individualQuantOpen}
        onClose={() => setIndividualQuantOpen(false)}
      />
      <DataIngestionMiddleIncomeModal
        open={middleincomeIngestionOpen}
        onClose={() => setMiddleIncomeIngestionOpen(false)}
      />
      <QuantAnalysisMiddleIncomeModal
        open={middleQuantOpen}
        onClose={() => setMiddleQuantOpen(false)}
      />
      {/* ✅ Video modal */}
      <LiveVideoModal
        open={liveVideoOpen}
        onClose={() => setLiveVideoOpen(false)}
        title={liveVideoTitle}
        url={liveVideoUrl}
        videoId={liveVideoId}
        category={liveVideoCategory}
      />
    </header>
  );
}
